import { InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import type { SpecialRatesQuery } from '../../../graphql/generated/graphql.ts';
import { SPECIAL_RATES_QUERY } from './SpecialRates.gql';
import { CreateSpecialRateModal } from './specialRates/CreateSpecialRateModal';
import { UpdateSpecialRateModal } from './specialRates/UpdateSpecialRateModal';

const { useToken } = theme;

export type SpecialRate = NonNullable<SpecialRatesQuery['labSpecialRates']>[number];

export const SpecialRates: React.FC = () => {
  const { token } = useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editSpecialRate, setEditSpecialRate] = useState<SpecialRate | null>(null);

  const { data, loading, refetch } = useQuery(SPECIAL_RATES_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<SpecialRate> = [
    {
      title: 'Kurzbezeichnung',
      dataIndex: 'shortName',
      key: 'shortName',
      ellipsis: true,
      width: 150,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.shortName.localeCompare(b.shortName),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </span>
      ),
    },
  ];

  const filteredSpecialRates: SpecialRate[] = useMemo(
    () => filterByTokens(data?.labSpecialRates ?? [], debouncedSearch, [s => s.name, s => s.shortName, s => s.code]),
    [debouncedSearch, data]
  );

  return (
    <MainContent>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          allowClear
          autoFocus
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Kurzbezeichnung, Bezeichnung oder Code">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalVisible(true)}>
          Neuer Sondertarif / Zuweiserkasse
        </Button>
      </div>
      <Table<SpecialRate>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredSpecialRates}
        onRow={record => ({ onClick: () => setEditSpecialRate(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Sondertarifen`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateSpecialRateModal
        modalVisible={createModalVisible}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalVisible(false)}
        labId={labId ?? ''}
        specialRates={data?.labSpecialRates ?? []}
      />
      <UpdateSpecialRateModal
        specialRate={editSpecialRate}
        onSuccess={() => refetch()}
        onClose={() => setEditSpecialRate(null)}
        specialRates={data?.labSpecialRates ?? []}
      />
    </MainContent>
  );
};
